import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Chip,
  IconButton,
  Autocomplete,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  ReceiptLong as ReceiptLongIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { expenseService } from '../../services/expenseService';
import { uploadService } from '../../services/uploadService';
import dayjs from 'dayjs';
import DatePickerField from '../../components/DatePickerField';

const paymentMethods = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'bank_transfer', label: 'Transferencia Bancaria' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'other', label: 'Otro' }
];

const currencies = [
  { value: 'RUB', label: 'Rublo Ruso', symbol: '₽' },
  { value: 'USD', label: 'Dólar Americano', symbol: '$' },
  { value: 'EUR', label: 'Euro', symbol: '€' }
];

const statuses = [
  { value: 'pending', label: 'Pendiente', color: 'warning' },
  { value: 'approved', label: 'Aprobado', color: 'info' },
  { value: 'paid', label: 'Pagado', color: 'success' },
  { value: 'cancelled', label: 'Cancelado', color: 'error' }
];

const schema = yup.object({
  concept: yup.string().required('El concepto es requerido'),
  description: yup.string().nullable(),
  amount: yup.number()
    .required('El monto es requerido')
    .min(0.01, 'Monto debe ser mayor a 0')
    .typeError('Debe ser un número válido'),
  expense_date: yup.string()
    .required('La fecha es requerida')
    .test('not-future', 'La fecha no puede ser futura', (value) => {
      return dayjs(value).isSameOrBefore(dayjs(), 'day');
    }),
  currency: yup.string().required('La moneda es requerida'),
  category: yup.string().nullable(),
  payment_method: yup.string().nullable(),
  supplier: yup.string().nullable(),
  invoice_number: yup.string().nullable(),
  status: yup.string().required('El estado es requerido'),
  comments: yup.string().nullable(),
  receipt_url: yup.string().nullable()
});

const ExpenseForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<{value: string, label: string, description: string}[]>([]);
  const [previousCategory, setPreviousCategory] = useState<string | null>(null);
  
  // Estados para archivo
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      concept: '',
      description: '',
      amount: 0,
      expense_date: dayjs().format('YYYY-MM-DD'),
      currency: 'RUB',
      category: '',
      payment_method: 'bank_transfer',
      supplier: '',
      invoice_number: '',
      status: 'paid',
      comments: '',
      receipt_url: ''
    }
  });

  const selectedCategory = watch('category');

  useEffect(() => {
    loadCategories();
    if (isEdit) {
      loadExpense();
    }
  }, [id]);

  useEffect(() => {
    if (selectedCategory && categories[selectedCategory]) {
        const categoryDescription = categories[selectedCategory];
        const currentDescription = watch('description');
        
        // Solo auto-llenar si la descripción está vacía O la categoría cambió
        if (!currentDescription || (selectedCategory !== previousCategory && !currentDescription.includes(categoryDescription))) {
        setValue('description', categoryDescription, { shouldDirty: false, shouldValidate: false });
        setPreviousCategory(selectedCategory);
        }
    }
  }, [selectedCategory, categories, setValue, watch, previousCategory]);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await expenseService.getCategories();
      setCategories(response.data);
      const options = Object.entries(response.data).map(([key, value]) => ({
        value: key,
        label: key,
        description: value as string
      }));
      setCategoryOptions(options);
    } catch (err) {
      console.error('Error loading categories:', err);
      setCategories({
        'Salarios': 'Pagos a profesores y personal',
        'Materiales': 'Materiales educativos y de oficina',
        'Servicios': 'Servicios públicos, internet, etc.',
        'Mantenimiento': 'Mantenimiento de equipos e instalaciones',
        'Marketing': 'Publicidad y promoción',
        'Eventos': 'Eventos escolares y actividades',
        'Tecnología': 'Equipos y software',
        'Otros': 'Otros gastos'
      });
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadExpense = async () => {
    try {
      setLoading(true);
      const response = await expenseService.getById(parseInt(id!));
      const data = response.data;
      reset(data);
      if (data.receipt_url) {
        setUploadedFile({
          name: 'Comprobante existente',
          url: data.receipt_url,
          type: 'application/pdf'
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar el gasto');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setError('Solo se permiten imágenes (JPEG, PNG, GIF) y archivos PDF');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo no puede ser mayor a 5MB');
        return;
      }
      setReceiptFile(file);
      setError(null);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleFileUpload = async (file: File): Promise<string> => {
    try {
      setUploadingFile(true);
      const response = await uploadService.uploadPaymentProof(file);
      const fileUrl = response.data.file_url;
      
      setUploadedFile({
        name: file.name,
        url: fileUrl,
        type: file.type
      });
      
      setValue('receipt_url', fileUrl);
      return fileUrl;
    } catch (err: any) {
      throw new Error('Error al subir archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setReceiptFile(null);
    setFilePreview(null);
    setValue('receipt_url', '');
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError(null);

      let receiptUrl = data.receipt_url;
      if (receiptFile && !uploadedFile) {
        receiptUrl = await handleFileUpload(receiptFile);
      }

      const expenseData = {
        ...data,
        receipt_url: receiptUrl
      };

      if (isEdit) {
        await expenseService.update(parseInt(id!), expenseData);
      } else {
        await expenseService.create(expenseData);
      }

      setSuccess(true);
      setTimeout(() => navigate('/expenses'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar el gasto');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton component={Link} to="/expenses" sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Editar Gasto' : 'Nuevo Gasto'}
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ¡Gasto guardado exitosamente! Redirigiendo...
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Información del Gasto */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información del Gasto
                </Typography>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="concept"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Concepto *"
                          error={!!errors.concept}
                          helperText={errors.concept?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ReceiptLongIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="category"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          options={categoryOptions}
                          getOptionLabel={(option) => option.label}
                          isOptionEqualToValue={(option, value) => option.value === value?.value}
                          value={categoryOptions.find(opt => opt.value === field.value) || null}
                          onChange={(_, newValue) => {
                            field.onChange(newValue?.value || '');
                          }}
                          loading={loadingCategories}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Categoría"
                              error={!!errors.category}
                              helperText={errors.category?.message}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <CategoryIcon />
                                  </InputAdornment>
                                )
                              }}
                            />
                          )}
                          renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            return (
                              <li key={key} {...otherProps}>
                                <Box display="flex" flexDirection="column">
                                  <Typography variant="body1">
                                    {option.label}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.description}
                                  </Typography>
                                </Box>
                              </li>
                            );
                          }}
                          noOptionsText="No hay categorías disponibles"
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="amount"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Monto *"
                          error={!!errors.amount}
                          helperText={errors.amount?.message}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₽</InputAdornment>
                          }}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="currency"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.currency}>
                          <InputLabel>Moneda *</InputLabel>
                          <Select {...field} label="Moneda *">
                            {currencies.map((c) => (
                              <MenuItem key={c.value} value={c.value}>
                                <Box display="flex" alignItems="center">
                                  <span style={{ marginRight: 8 }}>{c.symbol}</span>
                                  {c.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.currency && (
                            <FormHelperText>{errors.currency.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {/*<Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="expense_date"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="date"
                          label="Fecha del Gasto *"
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.expense_date}
                          helperText={errors.expense_date?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>*/}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="expense_date"
                      control={control}
                      render={({ field }) => (
                        <DatePickerField
                          {...field}
                          label="Fecha de Ingreso"
                          error={!!errors.expense_date}
                          helperText={errors.expense_date?.message}
                          disabled={loading}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="payment_method"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Método de Pago</InputLabel>
                          <Select {...field} label="Método de Pago">
                            {paymentMethods.map((m) => (
                              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="supplier"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Proveedor / Destinatario"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BusinessIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="invoice_number"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Número de Factura"
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.status}>
                          <InputLabel>Estado *</InputLabel>
                          <Select {...field} label="Estado *">
                            {statuses.map((s) => (
                              <MenuItem key={s.value} value={s.value}>
                                <Chip label={s.label} color={s.color as any} size="small" />
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.status && (
                            <FormHelperText>{errors.status.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={2}
                          label="Descripción"
                          placeholder="Detalles adicionales del gasto..."
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="comments"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={2}
                          label="Comentarios"
                          placeholder="Información adicional..."
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Comprobante de Pago */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Comprobante
                </Typography>

                <input
                  type="file"
                  id="receipt-upload"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                />

                {uploadedFile ? (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" alignItems="center">
                      <ReceiptIcon sx={{ fontSize: 40, mr: 2 }} />
                      <Box flex={1}>
                        <Typography variant="subtitle2">{uploadedFile.name}</Typography>
                        <Typography variant="caption" color="text.secondary">Comprobante cargado</Typography>
                      </Box>
                      <IconButton color="error" onClick={handleRemoveFile}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ) : receiptFile ? (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" alignItems="center">
                      {filePreview ? (
                        <Box
                          component="img"
                          src={filePreview}
                          sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1, mr: 2 }}
                        />
                      ) : (
                        <CloudUploadIcon sx={{ fontSize: 40, mr: 2 }} />
                      )}
                      <Box flex={1}>
                        <Typography variant="subtitle2">{receiptFile.name}</Typography>
                        <Typography variant="caption">{(receiptFile.size / 1024).toFixed(1)} KB</Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleFileUpload(receiptFile)}
                        disabled={uploadingFile}
                        sx={{ mr: 1 }}
                      >
                        {uploadingFile ? <CircularProgress size={20} /> : 'Subir'}
                      </Button>
                      <IconButton size="small" color="error" onClick={handleRemoveFile}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ) : (
                  <Paper
                    sx={{
                      border: '2px dashed #ccc',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' }
                    }}
                    onClick={() => document.getElementById('receipt-upload')?.click()}
                  >
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography>Haz clic para subir un comprobante</Typography>
                    <Typography variant="caption">Formatos: JPG, PNG, PDF (máx. 5MB)</Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Botones */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={() => navigate('/expenses')} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={loading || success}>
                {loading ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default ExpenseForm;